import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { SubscribeModal } from '@/components/ui/subscribe-modal'
import {
  MessageCircle,
  BookOpen,
  PenTool,
  Target,
  Play,
  Check,
  Lock,
  Crown,
  Sparkles,
} from 'lucide-react'
import type { LessonsCompleted, LessonKey } from '@/types/database'

// Lesson metadata mapping - includes all possible lesson types
const LESSON_METADATA: Record<LessonKey, {
  name: string
  shortName: string
  icon: typeof MessageCircle
}> = {
  warmup: {
    name: 'Chat',
    shortName: 'W',
    icon: MessageCircle,
  },
  vocabulary: {
    name: 'Vocab',
    shortName: 'V',
    icon: BookOpen,
  },
  grammar: {
    name: 'Grammar',
    shortName: 'G',
    icon: PenTool,
  },
  practice: {
    name: 'Practice',
    shortName: 'P',
    icon: Target,
  },
}

// Default lesson order for A1+ (can be overridden by lessonConfig prop)
const DEFAULT_LESSON_ORDER: LessonKey[] = ['warmup', 'vocabulary', 'grammar', 'practice']

export type UnitStatus = 'completed' | 'current' | 'locked' | 'premium-locked'

export interface RoadmapUnit {
  id: string
  task: string
  description: string | null
  order: number
  status: UnitStatus
  lessons?: LessonsCompleted
  lessonConfig?: LessonKey[]
}

export interface ChapterRoadmapProps {
  chapterNumber: number
  level: string
  units: RoadmapUnit[]
  isSubscribed: boolean
  lessonConfig?: LessonKey[]
  onStartLesson: (unitId: string, lessonType: string) => void
  onSubscribe: () => void
  onFreeConversation: () => void
}

// Check if lessonsCompleted is array format (new) or object format (legacy)
function isArrayFormat(lessonsCompleted: LessonsCompleted): lessonsCompleted is boolean[] {
  return Array.isArray(lessonsCompleted)
}

// Determine next lesson to start based on lessonConfig
function getNextLesson(lessonsCompleted: LessonsCompleted | undefined, lessonOrder: LessonKey[]): LessonKey | null {
  if (!lessonsCompleted) return lessonOrder[0] ?? null

  if (isArrayFormat(lessonsCompleted)) {
    // New format: boolean array parallel to lessonOrder
    const nextIndex = lessonsCompleted.findIndex(completed => !completed)
    if (nextIndex === -1) return null
    return lessonOrder[nextIndex] ?? null
  } else {
    // Legacy format: object with lesson keys
    for (const lesson of lessonOrder) {
      if (!lessonsCompleted[lesson]) {
        return lesson
      }
    }
    return null
  }
}

// Check if a lesson at given index is completed
function isLessonCompleted(lessonsCompleted: LessonsCompleted | undefined, lessonOrder: LessonKey[], index: number): boolean {
  if (!lessonsCompleted) return false

  if (isArrayFormat(lessonsCompleted)) {
    return lessonsCompleted[index] ?? false
  } else {
    const lessonKey = lessonOrder[index]
    return lessonKey ? (lessonsCompleted[lessonKey] ?? false) : false
  }
}

// Lesson dot component for the path within a unit
function LessonDot({
  lessonKey,
  isCompleted,
  isCurrent,
  isLocked,
  isPremiumLocked,
}: {
  lessonKey: LessonKey
  isCompleted: boolean
  isCurrent: boolean
  isLocked: boolean
  isPremiumLocked: boolean
}) {
  const meta = LESSON_METADATA[lessonKey]
  if (!meta) return null

  if (isPremiumLocked) {
    return (
      <div className="flex flex-col items-center">
        <div className="w-7 h-7 rounded-full border-2 border-violet-200 bg-violet-50/50 flex items-center justify-center">
          <span className="text-[10px] font-medium text-violet-300">{meta.shortName}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center">
      <div
        className={`
          w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300
          ${isCompleted
            ? 'bg-gradient-to-br from-emerald-400 to-teal-500 text-white shadow-md shadow-emerald-200/50'
            : isCurrent
              ? 'border-3 border-amber-400 bg-amber-100 animate-pulse-soft'
              : isLocked
                ? 'border-2 border-gray-200 bg-gray-50'
                : 'border-2 border-gray-200 bg-gray-50'
          }
        `}
      >
        {isCompleted ? (
          <Check className="w-3.5 h-3.5" strokeWidth={3} />
        ) : (
          <span className={`text-[10px] font-medium ${isCurrent ? 'text-amber-600' : 'text-gray-400'}`}>
            {meta.shortName}
          </span>
        )}
      </div>
    </div>
  )
}

// Path connecting lessons within a unit
function LessonPath({
  lessonsCompleted,
  unitStatus,
  lessonOrder,
}: {
  lessonsCompleted?: LessonsCompleted
  unitStatus: UnitStatus
  lessonOrder: LessonKey[]
}) {
  const isPremiumLocked = unitStatus === 'premium-locked'
  const isLocked = unitStatus === 'locked'

  // Find the index of the next lesson (for array format, we need to match by index, not just key)
  const nextLessonIndex = lessonsCompleted
    ? isArrayFormat(lessonsCompleted)
      ? lessonsCompleted.findIndex(completed => !completed)
      : lessonOrder.findIndex(key => !lessonsCompleted[key])
    : 0

  return (
    <div className="flex items-center justify-center gap-1 py-2">
      {lessonOrder.map((lessonKey, index) => {
        const isCompleted = isLessonCompleted(lessonsCompleted, lessonOrder, index)
        // For current lesson: must be at this index and unit status is 'current'
        const isCurrent = index === nextLessonIndex && unitStatus === 'current'
        const isLastLesson = index === lessonOrder.length - 1

        return (
          <div key={`${lessonKey}-${index}`} className="flex items-center">
            <LessonDot
              lessonKey={lessonKey}
              isCompleted={isCompleted}
              isCurrent={isCurrent}
              isLocked={isLocked}
              isPremiumLocked={isPremiumLocked}
            />
            {!isLastLesson && (
              <div
                className={`
                  w-4 h-0.5 mx-0.5 transition-all duration-300
                  ${isPremiumLocked
                    ? 'border-t border-dashed border-violet-200'
                    : isCompleted
                      ? 'bg-gradient-to-r from-emerald-400 to-teal-400'
                      : isCurrent
                        ? 'bg-gradient-to-r from-amber-300 to-amber-100'
                        : 'border-t border-dashed border-gray-200'
                  }
                `}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

// Status badge component
function StatusBadge({ status }: { status: UnitStatus }) {
  const badgeStyles = {
    completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    current: 'bg-amber-100 text-amber-700 border-amber-200',
    locked: 'bg-gray-100 text-gray-500 border-gray-200',
    'premium-locked': 'bg-violet-100 text-violet-600 border-violet-200',
  }

  const badgeText = {
    completed: 'Complete',
    current: 'Current',
    locked: 'Locked',
    'premium-locked': 'Premium',
  }

  const BadgeIcon = {
    completed: Check,
    current: Play,
    locked: Lock,
    'premium-locked': Crown,
  }[status]

  return (
    <span className={`
      inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border flex-shrink-0
      ${badgeStyles[status]}
    `}>
      <BadgeIcon className="w-2.5 h-2.5" />
      {badgeText[status]}
    </span>
  )
}

// Unit card component
function UnitCard({
  unit,
  unitNumber,
  lessonOrder,
  onStart,
  onPremiumClick,
}: {
  unit: RoadmapUnit
  unitNumber: number
  lessonOrder: LessonKey[]
  onStart: (lessonType: string) => void
  onPremiumClick: () => void
}) {
  // Use unit's lessonConfig if available, otherwise fall back to global lessonOrder
  const unitLessonOrder = unit.lessonConfig ?? lessonOrder
  const nextLesson = getNextLesson(unit.lessons, unitLessonOrder)
  const isPremiumLocked = unit.status === 'premium-locked'
  const isCurrent = unit.status === 'current'
  const isCompleted = unit.status === 'completed'

  const cardStyles = {
    completed: 'border-emerald-200/50 bg-gradient-to-br from-emerald-50/30 via-white to-teal-50/30',
    current: 'border-amber-300 bg-gradient-to-br from-amber-50/80 via-white to-orange-50/80 shadow-lg shadow-amber-100/50',
    locked: 'border-gray-200 bg-gray-50/50',
    'premium-locked': 'border-violet-200/50 bg-gradient-to-br from-violet-50/30 via-white to-purple-50/30',
  }

  return (
    <div
      className={`
        relative rounded-2xl border p-4 transition-all duration-300
        ${cardStyles[unit.status]}
        ${isPremiumLocked ? 'cursor-pointer hover:border-violet-300 hover:shadow-md' : ''}
        ${isCurrent ? 'ring-2 ring-amber-200/50' : ''}
      `}
      onClick={isPremiumLocked ? onPremiumClick : undefined}
    >
      {/* Unit header */}
      <div className="flex items-start gap-2 mb-3">
        {/* Unit number badge */}
        <div className={`
          w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
          ${isCompleted
            ? 'bg-gradient-to-br from-emerald-400 to-teal-500 text-white shadow-md shadow-emerald-200/50'
            : isCurrent
              ? 'bg-gradient-to-br from-amber-400 to-orange-400 text-white shadow-md shadow-amber-200/50'
              : isPremiumLocked
                ? 'bg-gradient-to-br from-violet-400 to-purple-400 text-white shadow-md shadow-violet-200/50'
                : 'bg-gray-200 text-gray-500'
          }
        `}>
          {isPremiumLocked ? (
            <Lock className="w-4 h-4" />
          ) : isCompleted ? (
            <Check className="w-5 h-5" strokeWidth={3} />
          ) : (
            <span className="text-lg font-bold">{unitNumber}</span>
          )}
        </div>

        {/* Unit info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            <h3 className={`font-semibold truncate flex-1 min-w-0 ${isPremiumLocked ? 'text-gray-600' : 'text-gray-800'}`}>
              {unit.task}
            </h3>
            <StatusBadge status={unit.status} />
          </div>
          {unit.description && (
            <p className={`
              text-xs mt-0.5 line-clamp-1
              ${isPremiumLocked ? 'text-gray-400 blur-[2px]' : 'text-gray-500'}
            `}>
              {unit.description}
            </p>
          )}
        </div>
      </div>

      {/* Lesson path */}
      <LessonPath lessonsCompleted={unit.lessons} unitStatus={unit.status} lessonOrder={unitLessonOrder} />

      {/* CTA for current unit */}
      {isCurrent && nextLesson && (
        <Button
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            onStart(nextLesson)
          }}
          className="w-full mt-2 h-10 font-semibold bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md shadow-amber-500/20 hover:shadow-lg hover:shadow-amber-500/25 transition-all duration-300 rounded-xl"
        >
          <Play className="w-4 h-4 mr-2" fill="currentColor" />
          Start {LESSON_METADATA[nextLesson].name}
        </Button>
      )}

      {/* Tap to unlock hint for premium */}
      {isPremiumLocked && (
        <p className="text-center text-xs text-violet-400 mt-2">
          Tap to unlock
        </p>
      )}
    </div>
  )
}

// Vertical connector between unit cards
function VerticalConnector({ isCompleted, isPremium }: { isCompleted: boolean; isPremium: boolean }) {
  return (
    <div className="flex justify-center py-1">
      <div className={`
        w-0.5 h-6 transition-all duration-300
        ${isPremium
          ? 'border-l border-dashed border-violet-200'
          : isCompleted
            ? 'bg-gradient-to-b from-emerald-400 to-teal-400'
            : 'border-l border-dashed border-gray-300'
        }
      `} />
    </div>
  )
}

// Premium upgrade banner
function UpgradeBanner({ onSubscribe }: { onSubscribe: () => void }) {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 p-4 text-white shadow-lg shadow-purple-500/25">
      {/* Shimmer effect */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 -translate-x-full animate-shimmer-sweep bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </div>

      {/* Sparkle decorations */}
      <Sparkles className="absolute top-3 right-3 w-5 h-5 text-white/50 animate-sparkle-twinkle" />
      <Sparkles className="absolute bottom-3 left-8 w-4 h-4 text-white/30 animate-sparkle-twinkle" style={{ animationDelay: '0.5s' }} />

      <div className="relative">
        <div className="flex items-center gap-2 mb-2">
          <Crown className="w-5 h-5" />
          <h3 className="font-bold text-lg">Unlock Your Full Journey</h3>
        </div>

        <p className="text-sm text-white/90 mb-3">
          Get access to all 6 units and continue your learning adventure.
        </p>

        <Button
          onClick={onSubscribe}
          className="w-full h-11 font-semibold bg-white text-purple-600 hover:bg-white/90 shadow-md transition-all duration-300 rounded-xl"
        >
          Subscribe Now
        </Button>
      </div>
    </div>
  )
}

export function ChapterRoadmap({
  chapterNumber,
  level,
  units,
  isSubscribed,
  lessonConfig,
  onStartLesson,
  onSubscribe,
  onFreeConversation,
}: ChapterRoadmapProps) {
  const [showSubscribeModal, setShowSubscribeModal] = useState(false)

  // Use provided lessonConfig or fall back to default
  const lessonOrder = lessonConfig ?? DEFAULT_LESSON_ORDER

  const completedCount = units.filter(u => u.status === 'completed').length
  const progressPercent = (completedCount / units.length) * 100
  const hasPremiumUnits = units.some(u => u.status === 'premium-locked')

  const handlePremiumClick = () => {
    setShowSubscribeModal(true)
  }

  return (
    <div className="relative">
      {/* Decorative background elements */}
      <div className="absolute -top-10 -left-10 w-40 h-40 bg-gradient-to-br from-amber-200/40 to-orange-200/40 rounded-full blur-3xl" />
      <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-gradient-to-br from-violet-200/30 to-purple-200/30 rounded-full blur-3xl" />

      {/* Main card */}
      <div className="relative bg-gradient-to-br from-amber-50/80 via-white to-orange-50/80 rounded-3xl border border-amber-200/50 shadow-xl overflow-hidden">
        {/* Top decorative bar */}
        <div className="h-2 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400" />

        <div className="p-6">
          {/* Chapter header */}
          <div className="mb-6 animate-fade-in">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                Chapter {chapterNumber} • {level === 'A0' ? 'Beginner' : level} English
              </h2>
              <span className="text-xs text-gray-500">
                {completedCount}/{units.length} units done
              </span>
            </div>

            {/* Progress bar */}
            <div className="h-2 rounded-full bg-amber-100 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-amber-400 to-orange-400 transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Units list */}
          <div className="space-y-0">
            {units.map((unit, index) => {
              const isLast = index === units.length - 1
              const nextUnit = units[index + 1]
              const isPrevCompleted = index === 0 || units[index - 1]?.status === 'completed'
              const isNextPremium = nextUnit?.status === 'premium-locked'

              return (
                <div key={unit.id} className="animate-fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                  <UnitCard
                    unit={unit}
                    unitNumber={index + 1}
                    lessonOrder={lessonOrder}
                    onStart={(lessonType) => onStartLesson(unit.id, lessonType)}
                    onPremiumClick={handlePremiumClick}
                  />
                  {!isLast && (
                    <VerticalConnector
                      isCompleted={unit.status === 'completed' && isPrevCompleted}
                      isPremium={isNextPremium}
                    />
                  )}
                </div>
              )
            })}
          </div>

          {/* Premium upgrade banner (only show if there are premium-locked units and user is not subscribed) */}
          {hasPremiumUnits && !isSubscribed && (
            <div className="mt-6 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <UpgradeBanner onSubscribe={() => setShowSubscribeModal(true)} />
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-amber-200/50 my-6" />

          {/* Free conversation link */}
          <div className="text-center animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <button
              onClick={onFreeConversation}
              className="text-amber-700 hover:text-amber-800 text-sm underline-offset-2 hover:underline transition-colors"
            >
              Or just have a conversation instead
            </button>
          </div>
        </div>
      </div>

      {/* Subscribe Modal */}
      <SubscribeModal
        isOpen={showSubscribeModal}
        onClose={() => setShowSubscribeModal(false)}
        onSubscribe={onSubscribe}
      />
    </div>
  )
}
